<template>
  <section class="storage settings-grid">
    <slot name="menu"></slot>
    <div class="settings-grid__main">
      <div class="settings-grid__main__header">
        <slot name="header">
          <h2>Banco de imagens</h2>
        </slot>
      </div>
      <Loader
        v-if="loading"
        color="#3E9AA6"
      />
      <div
        v-else-if="imageList.length > 0"
        ref="scroll"
        class="storage-content custom-scrollbar"
      >
        <ul class="media-list">
          <li
            v-for="(image, idx) in imageList"
            :key="idx"
            :class="{ expanded: idx === expandedIndex }"
            @click="handleItemSelect(image.url, idx, $event)"
          >
            <div class="img-wrapper">
              <img :src="s3Url(image.url)" />
            </div>
            <div
              v-if="idx === expandedIndex"
              class="content-wrapper"
            >
              <div class="content-wrapper__info">
                <h3>{{ mediaName(image.url) }}</h3>
                <p>
                  <strong>Tamanho da imagem original:</strong>
                  {{ formatBytes(image.size) }}
                </p>
                <p>
                  <strong>Data de upload:</strong>
                  {{ formatDate(image.lastModified) }}
                </p>
                <p>
                  <strong>URL do arquivo:</strong>
                  <a
                    :href="image.url"
                    target="_blank"
                  >
                    {{ image.url }}
                  </a>
                </p>
                <div class="content-wrapper__buttons">
                  <button
                    class="btn btn-danger"
                    :disabled="lookingForUses"
                    @click="handleDelete(image)"
                  >
                    <Loader
                      v-if="lookingForUses"
                      loader-height="36px"
                      color="#FFFFFF"
                    />
                    <span v-else>Excluir da biblioteca</span>
                  </button>
                </div>
              </div>
            </div>
          </li>
        </ul>
        <span class="total-items"
          >{{ imageList.length }} de {{ totalItems }} imagens</span
        >
        <button
          v-if="imageList.length < totalItems"
          class="btn btn-secondary see-more"
          :disabled="loading"
          @click="seeMore"
        >
          Ver mais
        </button>
      </div>
      <div
        v-else
        class="settings-grid__main__empty-state"
      >
        Você ainda não fez nenhum upload 😿
      </div>
    </div>

    <div
      v-show="!lookingForUses && isDeleteModalOpen"
      class="delete-midia-modal"
      @click.self="isDeleteModalOpen = false"
    >
      <div class="delete-midia-modal__content">
        <div class="delete-midia-modal__content__header">
          <label
            class="btn-close"
            @click="isDeleteModalOpen = false"
          >
            <IconClose />
          </label>
          <IconDelete
            class="text-danger"
            size="lg"
          />
          <h2>Deseja excluir permanentemente a imagem selecionada?</h2>
        </div>

        <div
          v-if="imageUsage.inUse"
          class="delete-midia-modal__content__image-in-use"
        >
          <p>Esta imagem está sendo utilizada nas seguintes páginas:</p>
          <div class="uses custom-scrollbar">
            <div v-if="imageUsage.pages.length > 0">
              <p
                v-for="page in imageUsage.pages"
                :key="page"
              >
                <router-link :to="`editor/${page}`">
                  {{ siteHostname }}/{{ page }}
                </router-link>
              </p>
            </div>
            <div v-if="imageUsage.modals.length > 0">
              <p
                v-for="modal in imageUsage.modals"
                :key="modal"
              >
                {{ modal }}
              </p>
            </div>
            <div v-if="imageUsage.favicon">
              <p>Favicon do site</p>
            </div>
          </div>
        </div>

        <div class="delete-midia-modal__content__buttons">
          <button
            class="btn btn-neutral"
            type="button"
            @click="isDeleteModalOpen = false"
          >
            Voltar
          </button>
          <button
            class="btn btn-danger"
            type="submit"
            :disabled="isDeletingImage"
            @click="deleteImage(itemBeingDeleted)"
          >
            <Loader
              v-if="isDeletingImage"
              loader-height="36px"
              color="#FFFFFF"
            />
            <span v-else>Excluir permanentemente</span>
          </button>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import { mapGetters, mapState } from 'vuex';

import getFormattedDate from '@/helpers/getFormattedDate';
import Loader from '@/components/Loader.vue';
import IconClose from '@/components/icons/IconClose.vue';
import IconDelete from '@/components/icons/IconDelete.vue';

export default {
  name: 'Storage',
  components: {
    Loader,
    IconClose,
    IconDelete,
  },
  data() {
    return {
      loading: false,
      storagePage: 1,
      expandedIndex: -1,
      lookingForUses: false,
      imageUsage: {},
      itemBeingDeleted: null,
      isDeleteModalOpen: false,
      isDeletingImage: false,
    };
  },
  computed: {
    imageList() {
      return this.imageStock?.slice(0, 30 * this.storagePage) || [];
    },
    totalItems() {
      return this.imageStock.length;
    },
    ...mapState(['imageStock']),
    ...mapGetters(['siteHostname']),
  },
  async created() {
    this.loading = true;

    try {
      await this.$store.dispatch('loadMediaStorage');
    } catch (err) {
      console.warn(err);
    }

    this.loading = false;
  },
  methods: {
    formatDate: getFormattedDate,
    formatBytes(bytes) {
      if (bytes === 0) {
        return '0 Bytes';
      }

      const kibibyte = 1024;
      const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
      const index = Math.floor(Math.log(bytes) / Math.log(kibibyte));
      const formattedSize = parseFloat((bytes / kibibyte ** index).toFixed(2));

      return `${formattedSize} ${sizes[index]}`;
    },
    s3Url(imageUrl) {
      return imageUrl.match(/https:\/\/letsimage.*/)[0];
    },
    mediaName(imageUrl) {
      return imageUrl.substring(imageUrl.lastIndexOf('/') + 1);
    },
    handleItemSelect(imageUrl, index, event) {
      if (this.$route.name === 'settings') {
        this.expandItem(index, event);
      } else {
        this.$emit('select', imageUrl);
      }
    },
    expandItem(idx, event) {
      if (idx === this.expandedIndex) {
        return;
      }

      this.expandedIndex = idx;

      this.$nextTick(() => {
        const { offsetTop } = event.currentTarget;
        const { scroll } = this.$refs;

        (scroll || window).scrollTo({
          top: offsetTop,
          behavior: 'smooth',
        });
      });
    },
    seeMore() {
      this.storagePage += 1;
    },
    handleDelete(image) {
      this.findImageUses(image);
      this.isDeleteModalOpen = true;
      this.itemBeingDeleted = image;
    },
    async deleteImage(image) {
      this.isDeletingImage = true;
      try {
        await this.$store.dispatch('deleteImage', image);
      } catch (err) {
        console.warn(err);
      }
      this.isDeleteModalOpen = false;
      this.isDeletingImage = false;
      this.expandedIndex = -1;
    },
    async findImageUses(image) {
      this.lookingForUses = true;
      try {
        this.imageUsage = await this.$store.dispatch('findImageUses', image);
      } catch (err) {
        console.warn(err);
      }
      this.lookingForUses = false;
    },
  },
};
</script>

<style lang="scss" scoped>
.settings-grid {
  &__main {
    &__empty-state {
      font-size: $size-normal;
      display: flex;
      align-items: center;
      justify-content: center;
      flex-grow: 1;
    }
  }
}

.storage {
  .storage-content {
    padding-top: 10px;
    position: relative;

    .media-list {
      gap: 20px;
      display: flex;
      flex-wrap: wrap;
      justify-content: flex-start;

      li {
        max-width: 120px;
        display: flex;
        align-items: center;
        flex-direction: column;
        cursor: pointer;
        overflow: hidden;

        .img-wrapper {
          width: 120px;
          height: 120px;
          border-radius: 8px;
          border: 2px solid $gray-300;
          overflow: hidden;
          background-color: #f5f5f5;

          img {
            width: 100%;
            height: 100%;
            object-fit: contain;
          }
        }

        .content-wrapper {
          padding: 20px;
          display: flex;
          flex-direction: column;
          justify-content: space-between;

          &__info {
            display: flex;
            flex-direction: column;
            white-space: wrap;
            word-break: break-all;

            h3 {
              font-size: $size-big;
              margin-bottom: 36px;
            }

            p {
              + p {
                margin-top: 18px;
              }

              strong {
                margin-right: 12px;
              }
            }
          }

          &__buttons {
            margin-top: 18px;

            > button {
              width: 155px;
              height: 35px;
            }
          }
        }

        .media-name {
          width: 100%;
          overflow: hidden;
          text-align: center;
          white-space: nowrap;
          text-overflow: ellipsis;
        }

        &.expanded {
          width: 100%;
          max-width: 100%;
          padding: 40px 0;
          cursor: initial;
          flex-direction: row;
          border: none;

          .img-wrapper {
            min-width: 502px;
            min-height: 340px;
            border-radius: 8px;
            overflow: hidden;
            background-color: initial;

            img {
              border-radius: 8px;
            }
          }
        }

        &:not(.expanded):hover .img-wrapper {
          border: 2px solid $primary;
        }
      }
    }

    .btn.see-more {
      display: block;
      margin: 20px auto 0;
    }

    .total-items {
      display: block;
      margin-top: 20px;
      text-align: center;
    }
  }

  .delete-midia-modal {
    position: fixed;
    top: 0;
    bottom: 0;
    right: 0;
    left: 0;
    background-color: rgba($lb-dark-gray, 0.4);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 1999;

    &__content {
      width: 545px;
      display: flex;
      padding: 40px;
      flex-direction: column;
      background-color: $lb-white;
      align-items: center;

      &__header {
        display: flex;
        justify-content: center;
        align-items: center;
        width: 100%;
        flex-direction: column;
        position: relative;

        h2 {
          font-size: $size-bigger;
          font-weight: $lb-medium;
          line-height: 35px;
          text-align: center;
          letter-spacing: -1px;
          color: $gray-900;
          margin: 28px 0 20px 0;
        }

        .btn-close {
          position: absolute;
          top: 0;
          right: 0;
          cursor: pointer;
        }
      }

      &__image-in-use {
        width: 100%;

        > p {
          font-size: $size-normal;
          text-align: center;
          margin-bottom: 20px;
        }

        .uses {
          padding: 10px;
          max-height: 220px;
          background: $gray-10;
          border: 1px solid $gray-40;
          border-radius: 8px;

          > div {
            + div {
              margin-top: 10px;
            }

            > p {
              font-size: $size-normal;
              word-break: break-all;
            }
          }
        }
      }

      &__buttons {
        width: 100%;
        margin-top: 28px;
        display: flex;
        justify-content: center;
        align-items: center;

        > button {
          width: 100%;
          height: 38px;

          + button {
            margin-left: 12px;
          }
        }
      }
    }
  }
}
</style>
